/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup, UntypedFormGroup } from '@angular/forms';
import { ChildrenOutletContexts, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

export interface HasForm {
  form: FormGroup | UntypedFormGroup | AbstractControl;
}

export interface HasUnsavedChanges {
  unsavedChanges?: boolean;
}

export interface HasOptionalUnreadReadLeaveWarning {
  shouldWarnLeaveWithoutConfirmRead(): boolean;
}

export interface HasCanDeactivate {
  canDeactivate(): boolean | Promise<boolean>;
}

@Injectable({ providedIn: 'root' })
export class UnsavedDataGuard {
  constructor(
    private translate: TranslateService,
    private router: Router,
    private outletContexts: ChildrenOutletContexts
  ) {}
  canDeactivate(
    component: HasForm | HasUnsavedChanges | HasOptionalUnreadReadLeaveWarning | HasCanDeactivate
  ): boolean | Promise<boolean> {
    if (window.history.state.skipUnsavedDataCheck) {
      window.history.state.skipUnsavedDataCheck = false;
      return true;
    }

    if (this.hasCanDeactivateHook(component)) {
      return component.canDeactivate();
    }

    // @ts-ignore
    if (component.unsavedChanges) {
      return this.confirmLeaveWithoutSaving();
    }
    // @ts-ignore
    if (!component.form) {
      // eslint-disable-next-line no-console
      console.warn(component.constructor.name, this.translate.instant('noFormProperty'));
      return true;
    }
    // @ts-ignore
    if (component.form.dirty && component.form.enabled) {
      return this.confirmLeaveWithoutSaving();
    }

    if (this.shouldWarnUnreadReadOnLeave(component)) {
      return this.confirmLeaveWithoutConfirmRead();
    }

    return true;
  }

  confirmLeaveWithoutSaving(): boolean {
    // eslint-disable-next-line no-restricted-globals, no-alert
    return confirm(this.translate.instant('confirmNavigationWithUnsavedData'));
  }

  confirmLeaveWithoutConfirmRead(): boolean {
    // eslint-disable-next-line no-restricted-globals, no-alert
    return confirm(this.translate.instant('confirmNavigationWithoutConfirmRead'));
  }

  private shouldWarnUnreadReadOnLeave(
    component: HasForm | HasUnsavedChanges | HasOptionalUnreadReadLeaveWarning | HasCanDeactivate
  ): boolean {
    const candidate = component as HasOptionalUnreadReadLeaveWarning;
    return (
      typeof candidate.shouldWarnLeaveWithoutConfirmRead === 'function' &&
      candidate.shouldWarnLeaveWithoutConfirmRead()
    );
  }

  checkUnsavedDataInCurrentComponent(): boolean | Promise<boolean> {
    const url = this.router.url.split('?')[0];
    const isAdminRoot = url === '/admin' || url === '/admin/';
    if (isAdminRoot) {
      return true;
    }

    const activeComponent = this.getActiveRoutedComponent();
    if (!activeComponent) {
      return true;
    }

    return this.canDeactivate(activeComponent);
  }

  async checkUnsavedDataInCurrentComponentAsync(): Promise<boolean> {
    const url = this.router.url.split('?')[0];
    const isAdminRoot = url === '/admin' || url === '/admin/';
    if (isAdminRoot) {
      return true;
    }

    const activeComponent = this.getActiveRoutedComponent();
    if (!activeComponent) {
      return true;
    }

    return await Promise.resolve(this.canDeactivate(activeComponent));
  }

  private hasCanDeactivateHook(
    component: HasForm | HasUnsavedChanges | HasOptionalUnreadReadLeaveWarning | HasCanDeactivate
  ): component is HasCanDeactivate {
    return typeof (component as HasCanDeactivate).canDeactivate === 'function';
  }

  private getActiveRoutedComponent(): HasForm | HasUnsavedChanges | HasCanDeactivate | null {
    let context = this.outletContexts.getContext('primary');
    while (context?.children?.getContext('primary')) {
      const childContext = context.children.getContext('primary');
      if (!childContext) {
        break;
      }
      context = childContext;
    }

    return context?.outlet?.component ?? null;
  }
}
